import React, { useMemo, useState, useRef, useEffect } from 'react';
import Navbar from './Navbar';
import ContactSection from './ContactSection';
import { gsap } from 'gsap';
import { usePageTransition } from './TransitionProvider';
import { projects, getAllTags, getAllYears, getProjectTags } from '../data/projects';
import Transition from './Transition';
import SEO from './SEO';

// Per-discipline accent colours — color-codes the work tags so the grid
// reads as categorised at a glance instead of one monochrome wall.
const TAG_COLORS = {
  'Design': '#b1c1ef',
  'Motion Graphics': '#f2acac',
  'Video': '#ffdd94',
  '3D': '#f97b22',
};
const tagColor = (tag) => TAG_COLORS[tag] || '#e4dbd2';

const WorksPage = () => {
  const { startWithImage } = usePageTransition();
  const [activeTag, setActiveTag] = useState('All');
  const [activeYears, setActiveYears] = useState([]);
  const [visibleCount, setVisibleCount] = useState(8);
  const gridRef = useRef(null);
  const imageRefs = useRef([]);

  // Dynamisch tags en jaren ophalen uit projecten data
  const chips = getAllTags();
  const years = getAllYears();

  const filtered = useMemo(() => {
    let result = projects;

    // Filter op tag
    if (activeTag !== 'All') {
      result = result.filter(i => getProjectTags(i).includes(activeTag));
    }

    // Filter op jaren als er geselecteerd zijn
    if (activeYears.length > 0) {
      result = result.filter(i => activeYears.includes(i.year));
    }

    result = [...result].sort((a, b) => {
      const yearDiff = Number(b.year) - Number(a.year);
      if (yearDiff !== 0) return yearDiff;
      return new Date(b.date) - new Date(a.date);
    });

    return result;
  }, [activeTag, activeYears]);

  // Toggle jaar selectie
  const toggleYear = (year) => {
    setActiveYears(prev =>
      prev.includes(year)
        ? prev.filter(y => y !== year)
        : [...prev, year]
    );
  };

  // Reset jaren wanneer tag verandert
  const handleTagChange = (tag) => {
    setActiveTag(tag);
    setActiveTag(tag);
    setActiveYears([]);
    setVisibleCount(8);
  };

  const loadMore = () => {
    setVisibleCount(prev => prev + 8);
  };

  const visibleProjects = filtered.slice(0, visibleCount);

  // Fade-out old, fade-in new with reflow
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const cards = Array.from(grid.children);
    gsap.killTweensOf(cards);
    gsap.fromTo(cards, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.4, stagger: 0.06, ease: 'power2.out' });
  }, [visibleProjects]);

  const handleProjectClick = (index, item) => {
    const imageElement = imageRefs.current[index];
    if (!imageElement) return;

    // Get the text elements to fade out
    const card = imageElement.closest('.project-card');
    const textElements = card ? card.querySelectorAll('.project-text') : [];

    const rect = imageElement.getBoundingClientRect();
    startWithImage(`/project/${item.id}`, {
      rect: {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height
      },
      imageSrc: item.image,
      projectData: item,
      textElements: Array.from(textElements)
    });
  };

  return (
    <>
      <SEO
        title="Work"
        description="Selected works by Sander Kuyken: branding, motion graphics, 3D / VFX, web design and illustration projects from 2008 onwards."
        path="/works"
      />
      <Navbar />
      <div className="bg-[#f2f3d9] min-h-[100vh]">
        <div className="w-[95vw] max-w-[1650px] mx-auto pt-[16vh] sm:pt-[18vh] lg:pt-[20vh]">
          {/* Header */}
          <div className="flex flex-col gap-4 sm:gap-5">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div className="font-anton text-[16vw] sm:text-[12vw] lg:text-[200px] leading-[0.89] tracking-[-4px] lg:tracking-[-8px] text-[#262624] text-left">
                WORK
              </div>
              <p className="font-space font-normal text-[15px] sm:text-[17px] lg:text-[20px] tracking-[-0.3px] lg:tracking-[-0.4px] text-left lg:text-right max-w-full lg:max-w-[638px] leading-[1.4] sm:leading-[1.25] lg:leading-[0.907]">
                A cross-section of client work and personal experiments — branding, motion, 3D and video. Filter by discipline or year to dig into the cases behind the thumbnails.
              </p>
            </div>
            <div className="h-px w-full bg-[#262624]" />
          </div>

          {/* Tag Chips */}
          <div className="mt-[36px] grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {['All', ...chips].map((c) => (
              <button
                key={c}
                onClick={() => handleTagChange(c)}
                className={`relative rounded-[40px] px-4 py-[10px] border uppercase font-bricolage font-extrabold text-[18px] sm:text-[20px] tracking-[-0.4px] flex items-center justify-center gap-2.5 transition-all duration-300 ease-in ${activeTag === c ? 'text-white bg-[#262624] border-[#262624]' : 'text-[#262624] bg-transparent border-[#262624] hover:text-white hover:bg-[#262624]'}`}
              >
                {c !== 'All' && (
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: tagColor(c) }}
                    aria-hidden="true"
                  />
                )}
                {c}
              </button>
            ))}
          </div>

          {/* Year Chips - alleen tonen als een specifieke tag geselecteerd is */}
          {activeTag !== 'All' && (
            <div className="mt-[20px] grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {years.map((year) => (
                <button
                  key={year}
                  onClick={() => toggleYear(year)}
                  className={`relative rounded-[40px] px-3 py-[8px] border font-bricolage font-bold text-[16px] tracking-[-0.2px] flex items-center justify-center transition-all duration-300 ease-in ${activeYears.includes(year) ? 'text-white bg-[#262624] border-[#262624]' : 'text-[#262624] bg-transparent border-[#262624] hover:text-white hover:bg-[#262624]'}`}
                >
                  {year}
                </button>
              ))}
            </div>
          )}

          {/* Result count + active filter context (Fase 5) */}
          <div className="mt-[28px] flex flex-wrap items-center gap-x-3 gap-y-2 font-space text-[#262624]/60 text-[13px] sm:text-sm uppercase tracking-[0.14em]">
            <span className="text-[#262624] font-semibold tabular-nums">{filtered.length}</span>
            <span>{filtered.length === 1 ? 'project' : 'projects'}</span>
            {activeTag !== 'All' && (
              <span className="inline-flex items-center gap-2 normal-case tracking-normal">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tagColor(activeTag) }} aria-hidden="true" />
                {activeTag}
              </span>
            )}
            {activeYears.length > 0 && (
              <span className="normal-case tracking-normal">· {activeYears.slice().sort((a, b) => b - a).join(', ')}</span>
            )}
          </div>

          {/* Grid */}
          <div ref={gridRef} className="mt-[24px] grid grid-cols-1 md:grid-cols-2 gap-x-[20px] gap-y-[48px] pb-[4vh]">
            {visibleProjects.map((item, idx) => {
              const tags = getProjectTags(item);
              const hoverImage = item.hoverImage ?? item.image;
              const isHoverVideo = typeof hoverImage === 'string' && /\.(webm|mp4|mov)$/i.test(hoverImage);
              return (
                <div
                  key={item.id ?? idx}
                  className={`project-card flex flex-col w-full max-w-full cursor-pointer group`}
                  onClick={() => handleProjectClick(idx, item)}
                >
                  <div className="relative h-[260px] sm:h-[320px] md:h-[460px] lg:h-[520px] rounded-[10px] w-full overflow-hidden bg-black flex-shrink-0">
                    {/* Tag rechtsboven - niet beïnvloed door hover */}
                    {tags.length > 0 && (
                      <div className="absolute top-3 right-3 z-20 flex flex-wrap gap-2 justify-end">
                        {tags.map((tag) => (
                          <div
                            key={tag}
                            className="inline-flex items-center justify-center h-[28px] sm:h-[32px] px-[10px] rounded-[4px] whitespace-nowrap shadow-sm"
                            style={{ backgroundColor: tagColor(tag) }}
                          >
                            <span className="font-anton text-[14px] sm:text-[16px] tracking-[0.4px] text-[#262624]">{tag}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Achtergrond afbeelding met blur effect bij hover */}
                    <img
                      ref={el => imageRefs.current[idx] = el}
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover transition-all duration-300 ease-out group-hover:blur-[8px]"
                    />

                    {/* Hover overlay met datum, afbeelding en titel */}
                    <div className="hidden md:flex absolute inset-0 items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-150 group-hover:duration-500 group-hover:delay-0 bg-black bg-opacity-20 overflow-hidden">
                      <div className="flex flex-col items-center justify-center text-center text-white">
                        {/* Datum bovenaan - van boven naar beneden */}
                        <div className="font-bricolage font-bold text-[36px] tracking-[-0.96px] mb-6 transform -translate-y-4 group-hover:translate-y-0 group-hover:transition-transform group-hover:duration-700 group-hover:delay-200 transition-transform duration-150">
                          {item.year}
                        </div>

                        {/* Project afbeelding - landscape ratio, scale van klein naar groot */}
                        <div className="w-[390px] h-[294px] rounded-[10px] overflow-hidden mb-6 transform scale-0 group-hover:scale-100 group-hover:transition-transform group-hover:duration-600 group-hover:delay-400 transition-transform duration-150 will-change-transform flex-shrink-0">
                          {isHoverVideo ? (
                            <video
                              src={hoverImage}
                              className="w-full h-full object-cover"
                              autoPlay
                              loop
                              muted
                              playsInline
                              preload="metadata"
                            />
                          ) : (
                            <img
                              src={hoverImage}
                              alt={item.title}
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>

                        {/* Titel onderaan - van beneden naar boven */}
                        <div className="project-text font-bricolage font-extrabold text-[28px] md:text-[32px] leading-[1.08] max-w-[620px] transform translate-y-4 group-hover:translate-y-0 group-hover:transition-transform group-hover:duration-700 group-hover:delay-600 transition-transform duration-150 tracking-[-0.8px] group-hover:tracking-[0.5px]">
                          {item.title}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Mobile info below image */}
                  <div className="flex md:hidden items-center justify-between mt-3">
                    <p className="font-bricolage font-semibold text-[18px] text-[#262624] max-w-[70%] leading-tight">
                      {item.title}
                    </p>
                    <span className="font-space text-[14px] text-[#686560]">
                      {item.year}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Load More Button */}
          {visibleCount < filtered.length && (
            <div className="flex justify-center pb-[12vh]">
              <button
                onClick={loadMore}
                className="group relative inline-flex items-center justify-center px-8 py-4 bg-[#262624] rounded-full overflow-hidden transition-transform duration-300 hover:scale-105"
              >
                {/* Gradient Hover Effect */}
                <div className="absolute inset-0 bg-gradient-to-l from-[#f97b22] from-[35.577%] to-[#fe9043] opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-out" />

                {/* Text */}
                <span className="relative z-10 font-bricolage font-extrabold text-[18px] sm:text-[20px] text-white uppercase tracking-[-0.4px]">
                  Load More
                </span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="pt-[40px]"><ContactSection /></div>
    </>
  );
};

export default Transition(WorksPage);


